/* eslint-disable no-unused-vars */

import React, { useCallback, useEffect, useState } from "react"
import { Controller, useForm } from "react-hook-form"
import { FormattedMessage, useIntl } from "react-intl"
import debounce from "lodash.debounce"
import { useCookies } from "react-cookie"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate, Navigate } from "react-router-dom"
import { toast } from "react-toastify"

// Components
import {
  PhoneNumberInput,
  Select,
  SignupInput,
  StaffNumberSelector,
} from "../../common"
import { BillingOptions } from "./components"

// Services
import {
  addCompanyInfo,
  addCompanyInfoForTrialUsers,
  isPhoneNumberFree,
} from "./../../../services/auth"

// Redux
import { loginAction } from "../../../redux/slices/authSlice"
import { selectIsFreemiumUser } from "../../../redux/selectors/selectIsFreemiumUser"

// Utils
import { getToken } from "../../../util/util"

// Constants
import { COUNTRIES, teamTypeOptions } from "../../../constants"

const industryOptions = [
  { value: "Finance", label: "common.finance" },
  { value: "HR Recruiters", label: "common.hrRecruiters" },
  { value: "IT/SaaS", label: "common.itSaaS" },
  { value: "Retail", label: "common.retail" },
  { value: "Services", label: "common.services" },
  { value: "Telecom", label: "common.telecom" },
  { value: "Transport", label: "common.transport" },
  { value: "Utilities", label: "common.utilities" },
  { value: "Others", label: "common.others" },
]

const CompanyInfoPage = () => {
  // eslint-disable-next-line no-unused-vars
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { formatMessage } = useIntl()
  const [_, setCookie] = useCookies(["token"])

  const signupForm = useSelector((state) => state.signup)
  const auth = useSelector((state) => state.auth)
  const isFreemium = useSelector(selectIsFreemiumUser)

  const [loading, setLoading] = useState(false)
  const [isValidPhoneNumber, setIsValidPhoneNumber] = useState(false)

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, touchedFields },
    setValue,
    // trigger,
    setError,
    clearErrors,
    watch,
    setFocus,
  } = useForm({
    mode: "onBlur",
    defaultValues: {
      teamType: teamTypeOptions.find(option => option.value === auth.team?.type) || teamTypeOptions[0],
      name: auth.team?.name || signupForm.company || "",
      position: auth.teamUser?.position || signupForm.position || "",
      countryCode: auth.user?.countryCode || "",
      phoneNumber: auth.user?.phoneNumber || signupForm.phoneNumber || "",
      type: auth.team?.type || signupForm.type || "Personal",
      industry: auth.team?.industry || "",
      isTrial: false,
      newHires: auth.team?.newHires || signupForm.newHires || "",
    },
  })

  // const values = getValues()

  const [
    teamType,
    name,
    countryCode,
    phoneNumber,
    industry,
    position,
    type,
    newHires,
  ] = watch([
    "teamType",
    "name",
    "countryCode",
    "phoneNumber",
    "industry",
    "position",
    "type",
    "newHires",
  ])

  useEffect(() => {
    if (isFreemium) {
      // Set preset values
      setValue("name", auth.team.name, { shouldTouch: true })
      setValue("phoneNumber", auth.user.phoneNumber, { shouldTouch: true })

      const country = COUNTRIES.find(
        (country) =>
          country.mobileCode === auth.user.countryCode &&
          country.code === auth.user.country,
      )

      if (country) {
        setValue(
          "countryCode",
          {
            label: country.mobileCode,
            value: country.code,
          },
          { shouldTouch: true },
        )
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Debounced useCallback function
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedCallback = useCallback(
    debounce((values) => {
      setLoading(true);
      isPhoneNumberFree({
        phoneNumber: values.phoneNumber,
        countryCode: values.countryCode,
      })
        .then(() => {
          setIsValidPhoneNumber(true);
          clearErrors();
        })
        .catch(() => {
          setError("phoneNumber", {
            message: formatMessage({
              id: "common.phoneNumberAlreadyRegistered",
            }),
          })
          setIsValidPhoneNumber(false)
        })
        .finally(() => setLoading(false));
    }, 1000), // Delay of the validation after typing
    [],
  )

  useEffect(() => {
    setIsValidPhoneNumber(false)

    if (!phoneNumber || !countryCode?.label) return

    debouncedCallback({
      phoneNumber: phoneNumber,
      countryCode: countryCode?.label,
    })
  }, [phoneNumber, countryCode?.label, debouncedCallback])

  const onFormSubmit = (values) => {
    if (!isValidPhoneNumber) return

    // Validate Phone Number
    if (!values.countryCode?.value) {
      return setError("countryCode", {
        message: formatMessage({
          id: "common.countryCodeRequired",
        }),
      })
    }

    setLoading(true)
    isPhoneNumberFree({
      phoneNumber: values.phoneNumber,
      countryCode: values.countryCode?.label,
    })
      .then(async () => {
        try {
          let data
          const isPersonalAccount = values.teamType.value === "Personal"

          const params = {
            company: values.name,
            position: values.position,
            phoneNumber: values.phoneNumber,
            organizationType: isPersonalAccount ? values.teamType.value : values.type,
            country: values.countryCode.value,
            countryCode: values.countryCode.label,
            newHires: values.newHires,
            industry: values.industry?.value || null,
          }

          if (isFreemium) {
            data = await addCompanyInfoForTrialUsers(params)
          } else {
            data = await addCompanyInfo({ ...params, token: getToken() })
          }

          setCookie("token", data.token, {
            path: "/",
            expires: new Date(Date.now() + 24 * 60 * 60 * 1000),
          })

          dispatch(loginAction(data))
          navigate("/choose-pricing")

        } catch (err) {
          toast.error(
            err?.response?.data?.message ||
              formatMessage({
                id: "toast.somethingWentWrong",
              }),
          )
        }
      })
      .catch(() => {
        setError("phoneNumber", {
          message: formatMessage({
            id: "common.phoneNumberAlreadyRegistered",
          }),
        })
        setFocus("phoneNumber")
      })
      .finally(() => {
        setLoading(false)
      })
  }

  if (!["AccountCreation", "Trialing", "ChoosePrice"].includes(auth.onboardingStage)) {
    return <Navigate to={"/login"} />
  }

  const personalAccountSelected = teamType.value === "Personal";
  const showFurtherValues =
    !personalAccountSelected &&
    !!name &&
    !!countryCode &&
    !!phoneNumber &&
    !!position;

  const newHiresNum = Number(newHires || 0)

  const choosingBillingOption =
    (personalAccountSelected &&
      !!name &&
      !!countryCode &&
      !!phoneNumber) ||
    (showFurtherValues &&
      type &&
      newHiresNum > 0)

  const idealBillingOption = newHiresNum > 100
    ? "Enterprise"
    : "PayAsYouGo"

  const teamTypeSelect = (
    <Controller
      control={control}
      name="teamType"
      rules={{
        required: formatMessage({ id: "common.teamTypeRequired" }),
      }}
      render={({ field: { onChange, value } }) => (
        <Select
          value={value}
          onChange={onChange}
          name={"teamType"}
          placeholder={formatMessage({ id: "common.teamType" })}
          options={teamTypeOptions}
          error={errors.teamType?.message}
        />
      )}
    />
  );

  const nameInput = (
    <SignupInput
      register={{
        ...register("name", {
          required: formatMessage({ id: "common.nameRequired" }),
          pattern: {
            value: /^[a-zA-Z0-9]+(?:[ -][a-zA-Z0-9]+)*$/,
            message: formatMessage({ id: "common.somethingDoesntLookRight" })
          },
        }),
      }}
      placeholder={formatMessage({
        id: `common.${personalAccountSelected ? "whatsYourName" : "whatsYourCompanyName"}`,
      })}
      error={errors.name?.message}
      touched={touchedFields.name}
      value={name}
      label={formatMessage({
        id: `common.${personalAccountSelected ? "name" : "company"}`,
      })}
    />
  );

  const phoneNumberInput = (
    <PhoneNumberInput
      errors={errors}
      isValid={isValidPhoneNumber}
      isValidating={loading}
      touchedFields={touchedFields}
      phoneNumberRegister={register("phoneNumber", {
        required: formatMessage({ id: "common.phoneNumberRequired" }),
        // Add validation to include only numbers
        pattern: {
          value: /^[0-9\s-]+$/,
          message: formatMessage({ id: "common.somethingDoesntLookRight" }),
        },
      })}
      control={control}
      countryCodeValue={countryCode}
      phoneNumberValue={phoneNumber}
      label={formatMessage({
        id: loading ? "common.validatingPhoneNumber" : "common.phoneNumber",
      })}
    />
  );

  const industryInput = (
    <Controller
      control={control}
      name="industry"
      render={({ field: { onChange, value } }) => (
        <Select
          value={value}
          onChange={onChange}
          name={"industry"}
          placeholder={formatMessage({ id: "common.industryOrSector" })}
          options={industryOptions}
          getOptionLabel={({ label }) => formatMessage({ id: label })}
          label={formatMessage({ id: "common.industry" })}
        />
      )}
    />
  );

  const positionInput = (
    <SignupInput
      register={register("position", {
        required: personalAccountSelected ? false : formatMessage({ id: "common.roleRequired" }),
        pattern: {
          value: /^[a-zA-Z -]+$/,
          message: formatMessage({ id: "common.somethingDoesntLookRight" })
        },
      })}
      placeholder={formatMessage({ id: "common.whatIsYourRoleThere" })}
      error={errors.position?.message}
      touched={touchedFields.position}
      value={position}
      label={formatMessage({ id: "common.role" })}
    />
  );

  const staffNumberSelect = (
    <div>
      <p className="create-account__label">
        <FormattedMessage id="common.numberOfStaff" />
      </p>
      <Controller
        control={control}
        name="type"
        render={({ field: { onChange, value } }) => (
          <StaffNumberSelector
            value={value}
            onChange={onChange}
          />
        )}
      />
    </div>
  );

  const newHiresInput = (
    <div className="mt-3">
      <p className="create-account__label">
        <FormattedMessage id="message.howManyPlanToHireNextYear" />
      </p>
      <SignupInput
        register={{
          ...register("newHires", {
            required: personalAccountSelected ? false : formatMessage({ id: "common.newHireCountRequired" }),
            valueAsNumber: true,
          }),
        }}
        type="number"
        min={1}
        step={1}
        placeholder={formatMessage({ id: "common.wellProbablyHireAround" })}
        error={errors.newHires?.message}
        touched={touchedFields.newHires}
        value={newHires}
        onWheel={(e) => e.currentTarget.blur()}
      />
    </div>
  );

  return (
    <>
      <div className="create-account">
        <div className="create-account__wrapper">

          <h1 className="create-account__title mb-8">
            <FormattedMessage id="message.findBestFitPlan" />
          </h1>

          <div className="create-account__content">
            <div className="create-account__inputs">
              {teamTypeSelect}

              {nameInput}
              {phoneNumberInput}
              {!personalAccountSelected && industryInput}
              {!personalAccountSelected && positionInput}

              {showFurtherValues && (
                <>
                  <p className="create-account__helper mt-3">
                    <FormattedMessage id="message.finalTwoQuestionsForMinCostPerHire" />
                  </p>

                  {staffNumberSelect}
                  {newHiresInput}

                  {choosingBillingOption && (
                    <>
                      <p className="create-account__title mt-6">
                        <FormattedMessage id="common.chooseAPlanOption" />
                      </p>
                      <p className="create-account__helper">
                        <FormattedMessage id="common.changeYourMindLater" />
                      </p>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {choosingBillingOption && (
          <BillingOptions
            setValue={setValue}
            loading={loading || !isValidPhoneNumber}
            onSelect={handleSubmit(onFormSubmit)}
            idealBillingOption={idealBillingOption}
          />
        )}
      </div>
    </>
  )
}

export default CompanyInfoPage
